import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { nanoid } from "nanoid";
import { match } from "ts-pattern";
import { useComputed, useSignal, useSignalEffect } from "@preact/signals";
import merge from "@tinkoff/utils/object/mergeDeep";
import omit from "@tinkoff/utils/object/omit";
import propSet from "@tinkoff/utils/object/propSet";
import stableSortBy from "@tinkoff/utils/array/stableSortBy";
import isEqual from "@tinkoff/utils/is/equal";
import { useEffect } from "react";
import classNames from "classnames";
import { FilterType } from "../../../lib/types/filters";
import DropDownMenu from "../../DropDownMenu";
import { filterCollection, hasSelectedValue, hasOptions, scopeFilter, mapCollection, } from "../../../lib/filter/filter-collection";
import DateFilter from "../DateFilter";
import KeyValueFilter from "../KeyValueFilter";
import ListFilter from "../ListFilter";
import MetricThresholdFilter from "../MetricThresholdFilter";
import TextFilter from "../TextFilter";
import { addInternalId, getFilterKey, joinFilters, splitFilters } from "../../../lib/filter/split-filters";
import style from "./style.module.scss";
export default function FilterSet(props) {
    const { compact = false, config, onChange, readOnly, selected = {} } = props;
    const filtersWithOptions = filterCollection(config, (filter) => {
        return filter.type === FilterType.TEXT || hasOptions(filter);
    });
    const filtersWithDefaultValue = filterCollection(filtersWithOptions, hasSelectedValue);
    const defaultSelected = Object.keys(selected).length ? selected : filtersWithDefaultValue;
    const selectedFilters = useSignal(splitFilters(defaultSelected));
    // A unique ID that is used to force re-renders of the filter components when the selected
    // values are changed from outside this component, e.g. from a back/forward navigation.
    const renderId = useSignal(nanoid());
    useEffect(() => {
        // When the `selected` prop changes, override the internal selected filters and force
        // a re-render by updating the render ID.
        if (selected) {
            // Ignore the $id props when comparing the selected filters, as these are non-deterministic
            // and will change on every render.
            const newSelected = splitFilters(selected);
            const newForCompare = Object.values(newSelected).map((f) => omit(["$id"], f));
            const currentSelected = Object.values(selectedFilters.peek()).map((f) => omit(["$id"], f));
            if (!isEqual(newForCompare, currentSelected)) {
                selectedFilters.value = newSelected;
                renderId.value = nanoid();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);
    // Once the filters have been updated, we can remove their default open state. This
    // prevents filters from being re-opened unexpectedly when the FilterSet re-renders.
    const removeAllOpenProps = (filters) => mapCollection(filters, (f) => omit(["open"], f));
    // "Commit" the internal state by notifying external listeners and updating the URL
    const updateSelectedFilters = (filters, removeOpenProp = true) => {
        if (readOnly) {
            return;
        }
        if (onChange) {
            const joinedFilters = joinFilters(filters);
            onChange(removeOpenProp ? removeAllOpenProps(joinedFilters) : joinedFilters);
        }
        selectedFilters.value = filters;
    };
    // Add a new filter pill
    const addFilter = (id) => {
        if (config[id]) {
            const newFilter = Object.assign(Object.assign({}, addInternalId(config[id])), { open: true });
            const key = getFilterKey(newFilter);
            updateSelectedFilters(merge(selectedFilters.peek(), { [key]: newFilter }), false);
        }
    };
    // Remove a staged filter pill
    const removeFilter = (key) => {
        updateSelectedFilters(omit([key], selectedFilters.peek()));
    };
    // Update the value of an staged filter
    const changeFilter = (key, selected) => {
        const currentFilters = removeAllOpenProps(selectedFilters.peek());
        const newFilterValue = Object.assign(Object.assign({}, currentFilters[key]), { selected });
        updateSelectedFilters(propSet(key, newFilterValue, currentFilters));
    };
    // Before rendering anything, we need to scope the filter options based on the selected filters
    const scopedFilters = useComputed(() => {
        const selectedFilterConfig = selectedFilters.value;
        return mapCollection(selectedFilterConfig, (filter) => {
            const key = getFilterKey(filter);
            return scopeFilter(config, selectedFilterConfig, selectedFilterConfig[key]);
        });
    });
    useSignalEffect(() => {
        // If scoping the filters resulted in any changes, we need to update the selected filters
        // to ensure there are no invalid selections.
        if (!isEqual(scopedFilters.value, selectedFilters.value)) {
            updateSelectedFilters(scopedFilters.value);
        }
    });
    const filterIsAvailable = (filter) => {
        if (filter.disabled || readOnly) {
            // The filter is not available if it is disabled or the FilterSet is read-only
            return false;
        }
        const key = getFilterKey(filter);
        if (key in selectedFilters.value && !filter.splitValues) {
            // The filter is not available if it is already selected and does not support splitting values
            return false;
        }
        return true;
    };
    // Compute which filters are available to be picked
    const availableFilterOptions = useComputed(() => {
        const availableFilters = filterCollection(filtersWithOptions, filterIsAvailable);
        const options = Object.values(availableFilters).map((filter) => ({
            label: filter.name,
            value: filter.id,
        }));
        return stableSortBy((o) => o.label, options);
    });
    // Sort the FilterCollection so that the non-removable filters are first
    const sortedSelectedFilters = useComputed(() => {
        const requiredFiltersFirst = stableSortBy((config) => Number(config.removable), Object.values(config));
        const selected = selectedFilters.value;
        // Retain the filter order by picking selected filters from the original config object, and
        // mapping those to the selected/scoped filters.
        const sorted = requiredFiltersFirst
            .filter((filter) => filter.id in selected)
            .map((filter) => selected[filter.id]);
        // The `sorted` array above is generated from the original config, so it does not include
        // filters that are keyed on their `$id`. Ensure these filters are included.
        const otherSelectedFilters = Object.values(selected).filter((filter) => !sorted.includes(filter));
        return [...sorted, ...otherSelectedFilters];
    });
    return (_jsx("div", { className: classNames("qa-filter-set", {
            [style["filter-set--compact"]]: compact,
        }), children: _jsxs("div", { className: style["filter-set__active-filters"], children: [sortedSelectedFilters.value.map((filter) => {
                    const key = getFilterKey(filter);
                    const onRemove = () => removeFilter(key);
                    const filterConfig = Object.assign(Object.assign({}, filter), { disabled: filter.disabled || readOnly, removable: filter.removable && !readOnly });
                    // The config's ID, available options, and selected options are used as the
                    // render key for the filter's node. This is to ensure the filter is
                    // re-rendered on scope changes or upstream changes like back-forward
                    // navigations.
                    return (_jsx("div", { children: getFilterComponent(filterConfig, changeFilter, onRemove) }, JSON.stringify([key, filter.options, renderId.value])));
                }), availableFilterOptions.value.length > 0 && (_jsx(DropDownMenu, { buttonLabel: "Add Filter", buttonClassName: classNames(style["filter-set__add-filter-button"], "qa-add-filter-button"), menuListClassName: "qa-add-filter-menu", sections: [{ title: "", options: availableFilterOptions.value }], onChange: (option) => addFilter(option.value) })), props.children] }) }));
}
function getFilterComponent(config, onChange, onRemove) {
    return match(config)
        .with({ type: FilterType.LIST }, (config) => (_jsx(ListFilter, { attributes: config.attributes, name: config.name, options: config.options, defaultValue: config.selected, allowCustomValues: config.allowCustomValues, removable: config.removable, disabled: config.disabled, multiple: config.multiple, open: config.open, onChange: (value) => onChange(getFilterKey(config), value), onRemove: onRemove })))
        .with({ type: FilterType.DATE_RANGE }, (config) => (_jsx(DateFilter, { attributes: config.attributes, name: config.name, options: config.options, defaultValue: config.selected, enableCompare: config.enableCompare, removable: config.removable, disabled: config.disabled, open: config.open, onChange: (value) => onChange(getFilterKey(config), value), onRemove: onRemove })))
        .with({ type: FilterType.KEY_VALUE }, (config) => (_jsx(KeyValueFilter, { attributes: config.attributes, name: config.name, options: config.options, defaultValue: config.selected, allowCustomValues: config.allowCustomValues, removable: config.removable, disabled: config.disabled, multiple: config.multiple, open: config.open, onChange: (value) => onChange(getFilterKey(config), value), onRemove: onRemove })))
        .with({ type: FilterType.THRESHOLD }, (config) => (_jsx(MetricThresholdFilter, { attributes: config.attributes, name: config.name, options: config.options, defaultValue: config.selected, removable: config.removable, disabled: config.disabled, open: config.open, onChange: (value) => onChange(getFilterKey(config), value), onRemove: onRemove })))
        .with({ type: FilterType.TEXT }, (config) => (_jsx(TextFilter, { attributes: config.attributes, name: config.name, options: config.options, defaultValue: config.selected, removable: config.removable, disabled: config.disabled, open: config.open, onChange: (value) => onChange(getFilterKey(config), value), onRemove: onRemove })))
        .exhaustive();
}
